/**
 * Synthetic audio feedback via Web Audio API.
 *
 *   chimeSuccess()   — selectable preset (bell / beep / chord / pop)
 *                      → "good scan / new order"
 *   chimeError()     — sawtooth descending buzz (A4 → E4)
 *                      → "bad scan / failed"
 *
 * Volume and preset are read from `uiStore` at call time so settings changes
 * take effect immediately. No assets shipped. iOS/Safari needs a user gesture
 * to start the AudioContext; `unlockAudio()` is wired into the first
 * pointerdown in <Shell>.
 */

import { useUIStore, type ChimePreset } from '@/stores/uiStore';

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  type AC = typeof AudioContext;
  const Ctor =
    (window.AudioContext as AC | undefined) ??
    ((window as unknown as { webkitAudioContext?: AC }).webkitAudioContext);
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

export function unlockAudio() {
  if (unlocked) return;
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  unlocked = true;
}

type Wave = 'sine' | 'square' | 'sawtooth' | 'triangle';

function ring(freq: number, start: number, duration: number, peak: number, type: Wave = 'sine') {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

interface PresetMeta {
  label: string;
  play: (volume: number) => void;
}

const PRESETS: Record<ChimePreset, PresetMeta> = {
  // Bright ascending two-tone sine — the original "barista bell".
  bell: {
    label: 'Bell',
    play: (vol) => {
      const c = getCtx();
      if (!c) return;
      const t = c.currentTime;
      ring(880, t, 0.45, 0.22 * vol);
      ring(1318.51, t + 0.08, 0.45, 0.16 * vol);
    },
  },
  // Utilitarian short square-wave beep.
  beep: {
    label: 'Beep',
    play: (vol) => {
      const c = getCtx();
      if (!c) return;
      const t = c.currentTime;
      ring(880, t, 0.18, 0.22 * vol, 'square');
    },
  },
  // Warm major triad chord (C5 + E5 + G5).
  chord: {
    label: 'Chord',
    play: (vol) => {
      const c = getCtx();
      if (!c) return;
      const t = c.currentTime;
      ring(523.25, t, 0.7, 0.12 * vol, 'triangle');
      ring(659.25, t, 0.7, 0.12 * vol, 'triangle');
      ring(783.99, t, 0.7, 0.12 * vol, 'triangle');
    },
  },
  // Quick two-note bubble pop, lower register, soft.
  pop: {
    label: 'Pop',
    play: (vol) => {
      const c = getCtx();
      if (!c) return;
      const t = c.currentTime;
      ring(440, t, 0.12, 0.22 * vol);
      ring(330, t + 0.07, 0.14, 0.18 * vol);
    },
  },
};

export const CHIME_PRESETS: { value: ChimePreset; label: string }[] = (
  Object.keys(PRESETS) as ChimePreset[]
).map((value) => ({ value, label: PRESETS[value].label }));

function currentVolume(): number {
  return useUIStore.getState().chimeVolume;
}

function currentPreset(): ChimePreset {
  return useUIStore.getState().chimePreset;
}

/** Play the user-selected success chime at their current volume. */
export function chimeSuccess(preset?: ChimePreset, volume?: number) {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  const p = preset ?? currentPreset();
  const v = volume ?? currentVolume();
  PRESETS[p].play(v);
}

/** Sawtooth descending buzz — used for failed pickup scans. Always the same tone. */
export function chimeError(volume?: number) {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  const v = volume ?? currentVolume();
  const t = c.currentTime;
  ring(440, t, 0.18, 0.20 * v, 'sawtooth');
  ring(329.63, t + 0.16, 0.22, 0.18 * v, 'sawtooth');
}

/** Back-compat alias — used by the WebSocket hook for new-order alerts. */
export const chime = chimeSuccess;

/**
 * Brief tactile pulse. `success` = single 50ms buzz, `error` = double buzz.
 * Vibrate API is no-op on iOS Safari and unsupported desktops.
 */
export function haptic(kind: 'success' | 'error') {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
  navigator.vibrate(kind === 'success' ? 50 : [60, 80, 60]);
}
